import React from "react";
import { twMerge } from "tailwind-merge";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { weightClassesName } from "../constants";
import { formatFirestoreTimestamp } from "../utils/common";

/**
 * Asked when an admin changes a fighter's weight and that fighter is sitting in
 * live registrations.
 *
 * Two rules drive the design:
 *
 *   - Matched registrations default to UNTICKED. Changing the class of a
 *     fighter who is already booked can void the bout, so the safe outcome is
 *     the one that happens if the admin stops reading and hits the button.
 *   - Applying never unmatches anyone. The pairing is kept and the bout is
 *     flagged for review instead.
 *
 * @param {boolean} open
 * @param {string} fighterName
 * @param {string|null} previousClass
 * @param {string|null} nextClass
 * @param {Array} registrations from fetchLiveRegistrationsForFighter()
 * @param {Set<string>} selectedIds
 * @param {(id:string)=>void} onToggle
 * @param {()=>void} onProfileOnly
 * @param {()=>void} onApply
 * @param {()=>void} onCancel
 * @param {boolean} saving
 */
export default function WeightPropagateModal({
    open,
    fighterName,
    previousClass,
    nextClass,
    registrations = [],
    selectedIds,
    onToggle,
    onProfileOnly,
    onApply,
    onCancel,
    saving = false,
}) {
    if (!open) return null;

    const selectedCount = registrations.filter((r) =>
        selectedIds?.has(r.id)
    ).length;
    const riskyCount = registrations.filter(
        (r) => selectedIds?.has(r.id) && r.matchWith
    ).length;

    return (
        <div className="modal modal-open">
            <div className="modal-box border max-w-lg">
                <h3 className="font-bold text-lg">Apply to registrations?</h3>
                <p className="text-sm opacity-70 mt-1">
                    {fighterName || "This fighter"} is in{" "}
                    <b>
                        {registrations.length} live registration
                        {registrations.length === 1 ? "" : "s"}
                    </b>
                    .{" "}
                    {previousClass ? (
                        <>
                            Weight class changed{" "}
                            <span className="font-mono">
                                {previousClass}{" "}
                                {weightClassesName[previousClass]}
                            </span>{" "}
                            →{" "}
                            <span className="font-mono font-bold">
                                {nextClass} {weightClassesName[nextClass]}
                            </span>
                            .
                        </>
                    ) : (
                        <>
                            New weight class{" "}
                            <span className="font-mono font-bold">
                                {nextClass} {weightClassesName[nextClass]}
                            </span>
                            .
                        </>
                    )}
                </p>

                <div className="flex flex-col gap-2 mt-4 max-h-[40vh] overflow-y-auto">
                    {registrations.map((reg) => {
                        const checked = !!selectedIds?.has(reg.id);
                        const fromClass = reg.eventWeightClass;
                        const movesClass = fromClass !== nextClass;
                        return (
                            <label
                                key={reg.id}
                                htmlFor={`prop-${reg.id}`}
                                className="flex flex-row items-start gap-3 border border-base-300 p-3 cursor-pointer hover:bg-base-200"
                            >
                                <input
                                    id={`prop-${reg.id}`}
                                    type="checkbox"
                                    className="checkbox checkbox-sm mt-1 shrink-0"
                                    checked={checked}
                                    onChange={() => onToggle?.(reg.id)}
                                />
                                <span className="min-w-0 flex-1">
                                    <span className="font-semibold block">
                                        {reg.eventName}
                                    </span>
                                    <span className="text-xs opacity-60 block">
                                        {formatFirestoreTimestamp(
                                            reg.eventDate
                                        ) || "No date set"}
                                        {reg.entry ? ` · ${reg.entry}` : ""}
                                    </span>
                                    <span className="text-xs font-mono mt-1 block">
                                        <span className="opacity-60 line-through">
                                            {fromClass
                                                ? `${fromClass} ${weightClassesName[fromClass]}`
                                                : "no class"}
                                        </span>{" "}
                                        →{" "}
                                        <span className="font-bold">
                                            {nextClass}{" "}
                                            {weightClassesName[nextClass]}
                                        </span>
                                    </span>
                                    {reg.matchWith ? (
                                        <span className="text-xs text-error flex flex-row gap-1 items-start mt-1">
                                            <AlertTriangle
                                                size={13}
                                                className="shrink-0 mt-[2px]"
                                            />
                                            <span>
                                                Matched vs{" "}
                                                {reg.opponentName ||
                                                    "an opponent"}
                                                {movesClass
                                                    ? " — the pairing is kept and flagged for review, never unmatched automatically"
                                                    : " — same class, pairing unaffected"}
                                            </span>
                                        </span>
                                    ) : (
                                        <span className="text-xs text-success flex flex-row gap-1 items-center mt-1">
                                            <CheckCircle2
                                                size={13}
                                                className="shrink-0"
                                            />
                                            Not matched yet — safe to move
                                        </span>
                                    )}
                                </span>
                            </label>
                        );
                    })}
                </div>

                <p
                    className={twMerge(
                        "text-xs mt-3",
                        riskyCount ? "text-warning" : "opacity-60"
                    )}
                >
                    {riskyCount
                        ? `${riskyCount} bout${
                              riskyCount === 1 ? "" : "s"
                          } will be flagged for rematch review.`
                        : "Matched registrations are unticked by default — the safe option is the one that happens if you stop reading."}
                </p>

                <div className="flex flex-col gap-2 mt-5">
                    <button
                        className="btn btn-primary w-full"
                        disabled={saving}
                        onClick={onApply}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : selectedCount === 0 ? (
                            "Update profile only"
                        ) : (
                            `Update profile + ${selectedCount} registration${
                                selectedCount === 1 ? "" : "s"
                            }`
                        )}
                    </button>
                    <div className="flex flex-row gap-2">
                        <button
                            className="btn btn-ghost flex-1"
                            disabled={saving}
                            onClick={onProfileOnly}
                        >
                            Profile only
                        </button>
                        <button
                            className="btn btn-ghost flex-1"
                            disabled={saving}
                            onClick={onCancel}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop" onClick={saving ? undefined : onCancel} />
        </div>
    );
}
